import {
  IncentiveCategory,
  IncentiveLifecycleStatus,
} from '@varo/graphql-schema-types';
import backgroundTextureLavender from 'assets/images/vxl/component/productIntroScreen/background-texture-lavender.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import IncentiveDisclosure from 'features/incentive/components/IncentiveDisclosure/IncentiveDisclosure';
import IncentiveProgramFallback from 'features/incentive/components/IncentiveProgramFallback/IncentiveProgramFallback';
import { INCENTIVE_HEADER_TOP_PADDING } from 'features/incentive/constants';
import { getIncentiveCopyPrefix } from 'features/incentive/helpers/getIncentiveCopyPrefix';
import {
  getIncentiveButtonAnalytics,
  IncentiveComponentNames,
  IncentiveErrorSteps,
  IncentiveScreenNames,
} from 'features/incentive/helpers/incentiveAnalytics';
import {
  useIncentiveAnalytics,
  useIncentiveScreenViewed,
} from 'features/incentive/hooks/useIncentiveAnalytics';
import useIncentiveProgram from 'features/incentive/hooks/useIncentiveProgram';
import useOptInToIncentive from 'features/incentive/hooks/useOptInToIncentive';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { VaroPressableNames } from 'shared/analytics/types/pressableNames';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import theme from 'shared/styles/theme';

const PREREQUISITE_REQUIRED_STATUSES = [
  IncentiveLifecycleStatus.Targeted,
  IncentiveLifecycleStatus.OptInPendingEligibility,
];

const OPTED_IN_STATUSES = [
  IncentiveLifecycleStatus.OptInPendingEligibility,
  IncentiveLifecycleStatus.Enrolled,
  IncentiveLifecycleStatus.InProgress,
];

interface IncentiveIntroScreenProps {
  incentiveType: IncentiveCategory;
  incentiveProgramId: string;
  lifecycleStatus: IncentiveLifecycleStatus;
  hasMetPrerequisite: boolean;
  onPrerequisiteRequired: () => void;
  onOptedIn: () => void;
}

function IncentiveIntroScreen({
  incentiveType,
  incentiveProgramId,
  lifecycleStatus,
  hasMetPrerequisite,
  onPrerequisiteRequired,
  onOptedIn,
}: IncentiveIntroScreenProps) {
  const router = useRouter();
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: getIncentiveCopyPrefix(incentiveType, 'intro'),
  });
  const { defaultMessage } = useDefaultErrorStrings();
  const { setSnackBarMessage } = useContext(GlobalUIContext);
  const programResult = useIncentiveProgram(incentiveType, incentiveProgramId);
  const { optInToIncentive, isOptingIn } = useOptInToIncentive();
  const { program, content } = programResult;
  const analyticsContext = {
    incentiveType,
    incentiveProgramId,
    lifecycleStatus,
  };
  const {
    logOptInSubmitted,
    logOptInCompleted,
    logErrorEncountered,
    logComponentViewed,
  } = useIncentiveAnalytics(analyticsContext);

  useIncentiveScreenViewed({
    screenName: IncentiveScreenNames.INTRO,
    isVisible: Boolean(program && content),
    ...analyticsContext,
  });

  const optIn = async () => {
    logOptInSubmitted();
    const outcome = await optInToIncentive(incentiveProgramId);
    if (!outcome) {
      return undefined;
    }

    if (!outcome.status) {
      logErrorEncountered({
        errorStep: IncentiveErrorSteps.OPT_IN,
        errorCode: outcome.error?.code,
      });
      setSnackBarMessage?.(outcome.error?.message ?? defaultMessage);
      return undefined;
    }

    const optedInStatus = outcome.status.status;
    if (!OPTED_IN_STATUSES.includes(optedInStatus)) {
      logErrorEncountered({
        errorStep: IncentiveErrorSteps.OPT_IN,
        errorCode: optedInStatus,
      });
      setSnackBarMessage?.(defaultMessage);
      onOptedIn();
      return undefined;
    }

    logOptInCompleted(optedInStatus);
    return optedInStatus;
  };

  const presentPrerequisiteSheet = () => {
    onPrerequisiteRequired();
    logComponentViewed(IncentiveComponentNames.PREREQUISITE_SHEET);
  };

  const handleJoinPress = async () => {
    const optedInStatus = await optIn();
    if (!optedInStatus) {
      return;
    }

    if (
      PREREQUISITE_REQUIRED_STATUSES.includes(lifecycleStatus) &&
      !hasMetPrerequisite
    ) {
      presentPrerequisiteSheet();
      return;
    }

    onOptedIn();
    if (optedInStatus === IncentiveLifecycleStatus.OptInPendingEligibility) {
      return;
    }
    router.push({
      pathname: '/incentive/[programId]/opted-in',
      params: { programId: incentiveProgramId },
    });
  };

  const handleHowItWorksPress = () => {
    router.push({
      pathname: '/incentive/[programId]/how-it-works',
      params: { programId: incentiveProgramId },
    });
  };

  if (!program || !content) {
    return (
      <IncentiveProgramFallback
        incentiveType={incentiveType}
        programResult={programResult}
      />
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={navigateBack}
              transparent
            />
          ),
        }}
      />
      <ProductIntroScreen
        visual={content.content.illustration}
        headlineTextStyle="expressive"
        backgroundTexture={backgroundTextureLavender}
        topPadding={INCENTIVE_HEADER_TOP_PADDING}
        eyebrowText={translatedText('eyebrow')}
        headlineText={translatedText('headline', {
          maxPrize: content.content.max_prize_display,
        })}
        bodyContent={
          <View style={styles.bodyContainer}>
            <BodyBaseText numberOfLines={0} style={styles.bodyText}>
              {translatedText('body')}
            </BodyBaseText>
          </View>
        }
        footerItem={
          <View style={styles.disclosureContainer}>
            <IncentiveDisclosure
              incentiveType={incentiveType}
              section="intro"
              program={program}
            />
          </View>
        }
        primaryButton={{
          label: translatedText('join'),
          onPress: handleJoinPress,
          isLoading: isOptingIn,
          componentAnalytics: getIncentiveButtonAnalytics(
            incentiveType,
            VaroPressableNames.INCENTIVE.JOIN_INCENTIVE,
          ),
        }}
        secondaryButton={{
          label: translatedText('howItWorks'),
          onPress: handleHowItWorksPress,
          isDisabled: isOptingIn,
          componentAnalytics: getIncentiveButtonAnalytics(
            incentiveType,
            VaroPressableNames.INCENTIVE.INCENTIVE_HOW_IT_WORKS,
          ),
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  bodyContainer: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  bodyText: {
    textAlign: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  disclosureContainer: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default IncentiveIntroScreen;
